module Visonum.BulkPingApp.Components.App
  ( Message(..)
  , component
  ) where

import Prelude

import Control.Monad.State.Class (get, modify_)
import Data.Argonaut as A
import Data.Codec.Argonaut as CA
import Data.DateTime.Instant (unInstant)
import Data.Duration (Duration)
import Data.Either (Either(..), isRight)
import Data.Identity (Identity)
import Data.Map as Map
import Data.Maybe (Maybe(..))
import Data.Newtype (unwrap)
import Data.Number.Format (fixed, toStringWith)
import Data.Time.Duration as DTD
import Data.Traversable (for_)
import Effect.Aff (Aff, Milliseconds(..), delay)
import Effect.Aff.Class (liftAff)
import Effect.Class (liftEffect)
import Effect.Class.Console (error, log)
import Effect.Now (now)
import Effect.Random (randomInt)
import Effect.Uncurried (EffectFn2, runEffectFn2)
import Halogen as H
import Halogen.HTML as HH
import Halogen.Styled as Styled
import Visonum.API.SpeedMaster as SpM
import Visonum.BulkPingApp.App.Error as Error
import Visonum.BulkPingApp.App.Header as Header
import Visonum.BulkPingApp.App.Login as Login
import Visonum.BulkPingApp.CSV (CSV)
import Visonum.BulkPingApp.CSV as CSV
import Visonum.BulkPingApp.Components.App.Types (Action(..), DownloadFormat(..), LogInState(..), State, parseInputVals)
import Visonum.BulkPingApp.DetailsPanel as DetailsPanel
import Visonum.BulkPingApp.EditPanel as EditPanel
import Visonum.BulkPingApp.MainPanel as MainPanel
import Visonum.BulkPingApp.StaticConfig (StaticConfig)
import Visonum.BulkPingApp.Util (dateTimeToStringISO8601)
import Visonum.BulkPingApp.Views.Layout as Layout
import Web.HTML (window)
import Web.HTML.Window (localStorage)
import Web.Storage.Storage (getItem, removeItem, setItem)

type Query = Identity

data Message = Void

type Input =
  { staticConfig :: StaticConfig
  }

component :: H.Component Query Input Message Aff
component =
  H.mkComponent
    { initialState
    , render
    , eval: H.mkEval $ H.defaultEval
        { handleAction = handleAction
        , initialize = Just Initialize
        , finalize = Just Finalize
        }
    }

initialState :: Input -> State
initialState { staticConfig } =
  { bulkPingStatus: { jobs: Map.empty }
  , staticConfig
  , showNewJob: false
  , newJob:
      { agentUrl: ""
      , pingCountPerIp: "3"
      , intervalBetweenIpsMillis: "1000"
      , intervalBetweenPingsMillis: "0"
      , useIcmp: true
      , useSyn: false
      , synPort: ""
      , destinationIps: ""
      }
  , spmApiKey: ""
  , logInState: LoginNotTried
  , details: Nothing
  , displayError: Nothing
  , pollingForkId: Nothing
  }

render :: forall m. State -> H.ComponentHTML Action () m
render state =
  HH.div []
    ( Styled.toHTML ctx $
        Layout.view
          { headerPanel: Header.view state
          , mainPanel: MainPanel.view state
          , editPanel: EditPanel.view state
          , detailsPanel: case state.details of
              Nothing -> Nothing
              Just _ -> Just $ DetailsPanel.view state
          , loginPanel: Login.view state
          , showLogin: case state.logInState of
              LoginNotTried -> true
              LoginChecking -> true
              LoginSuccess -> false
              LoginFailure -> true
          , error: case state.displayError of
              Nothing -> Nothing
              Just { title, message } ->
                Just $ Error.view { title, message, onClose: HideError }
          }
    )
  where
  ctx = { debug: true, staticConfig: state.staticConfig }

handleAction :: Action -> H.HalogenM State Action () Message Aff Unit
handleAction ac = do

  case ac of
    Initialize -> do
      mayKey <- liftEffect do
        win <- window
        store <- localStorage win
        getItem "key" store

      case mayKey of
        Just key -> do
          modify_ \state -> state
            { spmApiKey = key
            , logInState = LoginSuccess
            }
          startPolling
        Nothing -> pure unit

    Finalize -> do
      stopPolling

    HideError -> do
      modify_ \state -> state { displayError = Nothing }

    UpdateJobInput newJob -> do
      modify_ \state -> state { newJob = newJob }

    DownloadResults jobId fmt -> do
      state <- get
      c <- liftEffect $ randomInt 0 top
      result <- liftAff $ SpM.getBulkPingResult state.staticConfig.speedMasterUrl state.spmApiKey { jobId, c }
      case result of
        Left err -> do
          showError "Failed to fetch Result" (show err)
        Right data_ ->
          case fmt of
            DownloadJson -> do
              let json = CA.encode SpM.codecBulkPingResultResponse data_
              let str = A.stringify json
              liftEffect $ runEffectFn2 download ("bulk-ping-" <> jobId <> ".json") str

            DownloadCsv -> do
              let csv = pingsToCsv data_.results
              let str = CSV.printCsv csv
              liftEffect $ runEffectFn2 download ("bulk-ping-" <> jobId <> ".csv") str

    SetShowNewJob showNewJob -> do
      modify_ \state -> state { showNewJob = showNewJob }

    ShowDetails jobId -> do
      modify_ \state -> state { details = Just { jobId, result: Nothing } }

      state <- get
      
      c <- liftEffect $ randomInt 0 top
      resp <- liftAff $ SpM.getBulkPingResult state.staticConfig.speedMasterUrl state.spmApiKey { jobId, c }

      case resp of
        Left err -> do
          showError "Failed to fetch Result" (show err)
        Right _ -> do
          pure unit

      modify_ _ { details = Just { jobId, result: Just resp } }

    HideDetails -> do
      modify_ \state -> state { details = Nothing }

    SubmitNewJob -> do
      st <- get
      let result = parseInputVals st.newJob
      case result of
        Left err -> do
          showError "Invalid input" (show err)
        Right parsed -> do
          modify_ \state -> state { showNewJob = false }
          startResult <- liftAff $ SpM.postBulkPingStart st.staticConfig.speedMasterUrl st.spmApiKey parsed
          case startResult of
            Left err -> do
              showError "Failed to start job" (show err)
            Right _ -> do
              pure unit
          pure unit

    SetPassword pw -> do
      modify_ \state -> state { spmApiKey = pw }

    Login -> do
      modify_ \state -> state { logInState = LoginChecking }

      ok <- runSinglePoll

      if ok then do
        modify_ \state -> state { logInState = LoginSuccess }
        st <- get
        liftEffect do
          win <- window
          store <- localStorage win
          setItem "key" st.spmApiKey store

        startPolling
      else
        modify_ \state -> state { logInState = LoginFailure }

    Logout -> do
      modify_ \state -> state
        { logInState = LoginNotTried
        , spmApiKey = ""
        }
      liftEffect do
        win <- window
        store <- localStorage win
        removeItem "key" store

      stopPolling

    CancelJob jobId -> do
      st <- get
      _ <- liftAff $ SpM.postBulkPingCancel st.staticConfig.speedMasterUrl st.spmApiKey { jobId }
      pure unit

runPolling :: H.HalogenM State Action () Message Aff Unit
runPolling = do
  liftEffect $ log "Polling for bulk ping jobs"

  timeBefore <- liftEffect now

  _ <- runSinglePoll

  timeAfter <- liftEffect now

  let elapsedMs = ((unwrap $ unInstant timeAfter) - (unwrap $ unInstant timeBefore))

  liftAff $ delay $ Milliseconds (1000.0 - elapsedMs)

  runPolling

runSinglePoll :: H.HalogenM State Action () Message Aff Boolean
runSinglePoll = do
  { spmApiKey, staticConfig } <- get

  c <- liftEffect $ randomInt 0 top

  res <- liftAff $ SpM.getBulkPingStatus staticConfig.speedMasterUrl spmApiKey {c}

  case res of
    Left err -> liftEffect $ log ("Polling failed: " <> show err)
    Right status -> do
      modify_ \state -> state { bulkPingStatus = status }

  pure $ isRight res

foreign import download :: EffectFn2 String String Unit

showError :: String -> String -> H.HalogenM State Action () Message Aff Unit
showError title message = do
  modify_ \state -> state { displayError = Just { title, message } }
  error message
  pure unit

startPolling :: H.HalogenM State Action () Message Aff Unit
startPolling = do
  forkId <- H.fork do
    runPolling

  modify_ \state -> state { pollingForkId = Just forkId }

stopPolling :: H.HalogenM State Action () Message Aff Unit
stopPolling = do
  state <- get
  for_ state.pollingForkId \forkId -> do
    H.kill forkId
    modify_ _ { pollingForkId = Nothing }

pingsToCsv :: Array SpM.BulkPingEntry -> CSV
pingsToCsv entries =
  { headers:
      [ "Destination IP"
      , "ICMP Timestamp"
      , "ICMP Min (ms)"
      , "ICMP Avg (ms)"
      , "ICMP Median (ms)"
      , "ICMP Max (ms)"
      , "ICMP StdDev Jitter (ms)"
      , "ICMP TR143 Jitter (ms)"
      , "ICMP Error"
      , "TCP SYN Timestamp"
      , "TCP SYN Min (ms)"
      , "TCP SYN Avg (ms)"
      , "TCP SYN Median (ms)"
      , "TCP SYN Max (ms)"
      , "TCP SYN StdDev Jitter (ms)"
      , "TCP SYN TR143 Jitter (ms)"
      , "TCP SYN Error"
      ]
  , rows: map mkRow entries
  }

  where
  mkRow :: SpM.BulkPingEntry -> Array (Maybe String)
  mkRow { destination, timeIcmp, timeSyn } =
    [ Just destination ] <> cellsFromBulkPingResult timeIcmp <> cellsFromBulkPingResult timeSyn

cellsFromBulkPingResult :: Maybe SpM.BulkPingResult -> Array (Maybe String)
cellsFromBulkPingResult result =
  [ case result of
      Just (SpM.BulkPingSuccess r) -> Just $ dateTimeToStringISO8601 r.timestamp
      _ -> Nothing
  , case result of
      Just (SpM.BulkPingSuccess r) -> Just $ printDuration r.pingTimeMinMillis
      _ -> Nothing
  , case result of
      Just (SpM.BulkPingSuccess r) -> Just $ printDuration r.pingTimeAvgMillis
      _ -> Nothing
  , case result of
      Just (SpM.BulkPingSuccess r) -> Just $ printDuration r.pingTimeMedianMillis
      _ -> Nothing
  , case result of
      Just (SpM.BulkPingSuccess r) -> Just $ printDuration r.pingTimeMaxMillis
      _ -> Nothing
  , case result of
      Just (SpM.BulkPingSuccess r) -> Just $ printDuration r.pingTimeStdDevJitterMillis
      _ -> Nothing
  , case result of
      Just (SpM.BulkPingSuccess r) -> Just $ printDuration r.pingTimeTR143JitterMillis
      _ -> Nothing
  , case result of
      Just (SpM.BulkPingError r) -> Just r.message
      Nothing -> Just "N/A"
      _ -> Nothing
  ]

printDuration :: Duration -> String
printDuration = toStringWith (fixed 2) <<< unwrap <<< DTD.fromDuration
